import Button from "components/form/Button";
import InputField from "components/form/InputField";
import { SuccessPaymentIcon } from "icons/payments/successPayment";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserData } from "store/slices/authSlice";
import {
  decode,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import { useTestAttrsFactory } from "utils/testid";

interface InvoiceSuccessProps {
  className?: string;
  amount: number;
  emailInput: string;
  setEmailInput: any;
  invoiceId?: string;
  setId: any;
  setIsPreview: any;
  isEmailLoading: boolean;
  handleSendEmail: any;
  isDownloadLoading: boolean;
  handleDownloadPDF: any;
  orderId?: any;
}

const InvoiceSuccess = (props: InvoiceSuccessProps) => {
  const {
    className = "",
    amount,
    emailInput,
    setEmailInput,
    invoiceId,
    setId,
    setIsPreview,
    isEmailLoading,
    handleSendEmail,
    isDownloadLoading,
    handleDownloadPDF,
    orderId = "",
  } = props;
  const navigate = useNavigate();
  const { id: actionId } = useParams();
  let slug = decode(actionId || "")?.split("_");
  let cluster_id = encode(slug[1], "ci_");

  const userData = useUserData();
  const accountData = userData?.data?.accountData;
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const testAttrs = useTestAttrsFactory();

  const sendEmail = async (shouldSendAll: boolean = true) => {
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      return;
    }
    try {
      let payload = {
        invoice_id: orderId,
        accountid: accountData?.accountid,
        emails: [emailInput],
        shouldSendAll: shouldSendAll,
      };
      await handleSendEmail(payload);
      setEmailInput("");
    } catch (error) {
      console.error("Failed to send email:", error);
    }
  };

  return (
    <div
      className={`w-full h-full max-w-[620px] px-5 text-center ${className}`}
      {...testAttrs("success", "Invoice paid successfully")}
    >
      <div className="h-full flex flex-col justify-center items-center gap-24 ">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <SuccessPaymentIcon aria-hidden="true" />
          <div className="text-txtPrimary lg:text-2xl leading-8 text-xl font-medium">
            Invoice paid successfully
          </div>
          <div className="w-full flex flex-col justify-center items-center">
            <div className="w-full text-center font-normal bg-bgThird lg:py-5 py-4 lg:px-2 px-3 lg:text-lg text-base text-txtParagraph break-words">
              Your invoice payment of{" "}
              <span className="font-bold">
                {formatCurrency(amount) || "$0.00"}
              </span>{" "}
              has been paid.
            </div>
            <div className="lg:text-base text-sm text-txtParagraph pt-4">
              <span
                className="text-btnPrimaryActive hover:!text-btnPrimaryHover cursor-pointer"
                role="button"
                tabIndex={0}
                {...testAttrs("view-invoice-details", "View invoice details")}
                onClick={() => {
                  setId(invoiceId);
                  setIsPreview(true);
                }}
              >
                Click here
              </span>{" "}
              to view your full invoice details.
            </div>
          </div>
        </div>
        <div className="lg:px-3.5">
          <div className="flex-col flex justify-center items-center gap-[30px]">
            <div className="text-txtParagraph lg:text-base text-sm whitespace-normal break-words text-center">
              A copy of this invoice will automatically be sent to your email
              address. To send a copy to an additional email, enter the address
              below.
              {/* <span className="font-bold">{email}</span>. To send a copy to an
              additional email, enter the address below. */}
            </div>
            <div className="px-[10px] w-full">
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
                {...testAttrs(
                  "invoice-email",
                  "Additional email for invoice copy"
                )}
              />
            </div>
            <div className="m-[10px] w-full flex justify-center items-center gap-5">
              <Button
                text="Send Email"
                className="w-full"
                isLoading={isEmailLoading}
                isDisabled={emailInput.length === 0}
                {...testAttrs("send-invoice-email", "Send invoice email")}
                onClick={() => {
                  sendEmail(false);
                }}
              />
              <Button
                text="Download"
                className="w-full"
                isLoading={isDownloadLoading}
                {...testAttrs("download-invoice", "Download invoice")}
                onClick={handleDownloadPDF}
              />
            </div>
          </div>
          <div className="w-full lg:pb-0 pb-5 lg:text-base text-sm lg:pt-6 pt-5 text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
            <div
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              {...testAttrs("back-to-dashboard", "Back to dashboard")}
              onClick={() => navigate(`/${cluster_id}/dashboard`)}
            >
              Back to dashboard
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSuccess;
